import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FamilyTimeline = () => {
  const timelineEvents = [
    {
      year: "1987",
      title: "The Beginning",
      description: `Our founder, Hassan Mahmoud, arrived from the coastal town of Antalya, Turkey, carrying nothing but a worn leather recipe book filled with his grandmother's handwritten notes. These precious recipes, some dating back to the Ottoman Empire, would become the foundation of what we know today as Kebab Palace.`,
      image: "https://images.unsplash.com/photo-1577308856961-8e0ec50d0c4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Heart",
      side: "left"
    },
    {
      year: "1992",
      title: "First Restaurant Opens",
      description: `After five years of perfecting recipes and building relationships with local suppliers, Hassan opened the first Kebab Palace in a small 20-seat location. The authentic flavors and warm hospitality quickly attracted a loyal following of food enthusiasts seeking genuine Mediterranean cuisine.`,
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Store",
      side: "right"
    },
    {
      year: "1998",
      title: "Family Tradition Continues",
      description: `Hassan's son, Omar, joined the business after completing his culinary training in Istanbul. Together, they expanded the menu while maintaining the authentic preparation methods passed down through generations. The father-son duo became known for their dedication to traditional techniques and premium ingredients.`,
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Users",
      side: "left"
    },
    {
      year: "2005",
      title: "Community Recognition",
      description: `Kebab Palace received its first major recognition when the local food critics association awarded us 'Best Authentic Mediterranean Restaurant.' This milestone marked our transition from a neighborhood gem to a city-wide destination for authentic kebab cuisine.`,
      image: "https://images.unsplash.com/photo-1551218808-94e220e084d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Award",
      side: "right"
    },
    {
      year: "2012",
      title: "Expansion & Innovation",
      description: `While staying true to our roots, we embraced modern dining preferences by introducing online ordering and catering services. Our commitment to quality remained unchanged – every dish still prepared using the same traditional methods and family recipes that started our journey.`,
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Smartphone",
      side: "left"
    },
    {
      year: "2020",
      title: "Resilience & Adaptation",
      description: `During challenging times, our community support kept us strong. We pivoted to focus on delivery and takeout while maintaining our commitment to fresh, authentic flavors. Our customers' loyalty reminded us why we started this journey – to bring families together through food.`,
      image: "https://images.unsplash.com/photo-1588315029754-2dd089d39a1a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Truck",
      side: "right"
    },
    {
      year: "2024",
      title: "Legacy Continues",
      description: `Today, three generations of the Mahmoud family work together to preserve our culinary heritage while embracing modern conveniences. Our commitment remains the same: serving authentic Mediterranean flavors with the warmth and hospitality that makes every guest feel like family.`,
      image: "https://images.unsplash.com/photo-1577308856961-8e0ec50d0c4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Crown",
      side: "left"
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Our Family Heritage
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          From humble beginnings to becoming a beloved community institution, our story spans over three decades of dedication to authentic Mediterranean cuisine and warm hospitality.
        </p>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-primary-200 h-full hidden md:block"></div>

        {/* Timeline Events */}
        <div className="space-y-16">
          {timelineEvents.map((event, index) => (
            <motion.div
              key={event.year}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`flex flex-col md:flex-row items-center gap-8 ${
                event.side === 'right' ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className="flex-1 max-w-lg">
                <div className="card-surface">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <Icon name={event.icon} size={24} className="text-white" />
                    </div>
                    <div>
                      <span className="font-cta text-2xl font-bold text-primary">
                        {event.year}
                      </span>
                      <h3 className="font-headline text-xl font-semibold text-text-primary">
                        {event.title}
                      </h3>
                    </div>
                  </div>
                  <p className="font-body text-text-secondary leading-relaxed">
                    {event.description}
                  </p>
                </div>
              </div>

              {/* Timeline Dot */}
              <div className="hidden md:block relative">
                <div className="w-6 h-6 bg-primary rounded-full border-4 border-background shadow-warm"></div>
              </div>

              {/* Image */}
              <div className="flex-1 max-w-lg">
                <div className="relative overflow-hidden rounded-lg shadow-warm-lg">
                  <Image
                    src={event.image}
                    alt={event.title}
                    className="w-full h-64 object-cover transition-organic hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Heritage Quote */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="mt-20 text-center"
      >
        <div className="max-w-4xl mx-auto card-surface">
          <Icon name="Quote" size={48} className="text-primary mx-auto mb-6" />
          <blockquote className="font-accent text-2xl md:text-3xl text-text-primary mb-6 italic">
            "Every recipe tells a story, every meal creates a memory, and every guest becomes part of our extended family."
          </blockquote>
          <cite className="font-body text-lg text-text-secondary">
            — Hassan Mahmoud, Founder & Head Chef
          </cite>
        </div>
      </motion.div>
    </div>
  );
};

export default FamilyTimeline;